import React, { useEffect, useRef, useState } from "react";

import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";

import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";

import JobService from "../../service/job";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/actions";
import HistoryTable from "./HistoryTable";

const useStyles = makeStyles((theme) =>
  createStyles({
    paper: {
      height: "100%",
      padding: "10px 20px",
    },
    detailsHeader: {
      fontWeight: "normal",
      "&::after": {
        content: "''",
        display: "block",
        width: "40px",
        height: "3px",
        background: "#EC5143",
        margin: "5px 0 0",
        borderRadius: "5px",
      },
    },
  })
);

const columns = [
  {
    id: "approve_status",
    label: "Status",
    width: 100,
  },
  { id: "amount", label: "Amount", width: 100 },
  {
    id: "description",
    label: "Description",
    width: 200,
  },
  {
    id: "customer_description",
    label: "Customer Comments",
    width: 200,
  },
  {
    id: "user",
    label: "Updated By",
    width: 150,
  },
];

const Order = ({ job_id, amount, orderDescription, orderComments, setStatus, role, setValue }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [comments, setComments] = useState(orderComments);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [errors, setErrors] = useState({
    amount: false,
    description: false,
  });

  const amountRef = useRef(null);
  const orderDescriptionRef = useRef(null);

  useEffect(() => {
    setHistoryLoading(true);

    const Job = new JobService();
    Job.getHistory(job_id, "Order")
      .then((response) => {
        if (response.success) setHistory(response.data);

        setHistoryLoading(false);
      })
      .catch(() => {
        setHistoryLoading(false);
      });
  }, [job_id]);

  const handleOrder = () => {
    setErrors((prevState) => {
      return {
        ...prevState,
        amount: !amountRef.current || amountRef.current.value.trim() === "",
        description:
          !orderDescriptionRef.current ||
          orderDescriptionRef.current.value.trim() === "" ||
          orderDescriptionRef.current.value.trim().length > 500,
      };
    });

    if (
      !(
        !amountRef.current ||
        amountRef.current.value.trim() === "" ||
        !orderDescriptionRef.current ||
        orderDescriptionRef.current.value.trim() === "" ||
        orderDescriptionRef.current.value.trim().length > 500
      )
    ) {
      const statusValue = role == 3 ? 4 : 3;

      setLoading(true);
      const Job = new JobService();

      const formData = new FormData();
      formData.append("Amount", amountRef.current.value);
      formData.append("QuotationDescription", orderDescriptionRef.current.value);
      formData.append("Status", statusValue);
      formData.append("addStatus", "Order");

      Job.updateJob(job_id, formData).then((response) => {
        if (response.success) {
          setStatus(statusValue);
          setValue(0);
          dispatch(
            showToast({
              type: "success",
              message: "Job ordered successfully!",
            })
          );
        }

        setLoading(false);
      });
    }
  };

  const handleApproveRejectOrder = (type) => {
    setLoading(true);

    // If type is 1 that is Approve order then set status to 4 (Artwork in process)
    // Else set status to 2 (Awaiting Order)
    const statusValue = type === 1 ? 4 : 2;
    const Job = new JobService();

    const formData = new FormData();
    formData.append("Amount", amount);
    formData.append("QuotationDescription", orderDescription);
    formData.append("QuotationComments", comments);
    formData.append("Status", statusValue);
    formData.append("addStatus", "Order");

    Job.updateJob(job_id, formData).then((response) => {
      if (response.success) {
        setStatus(statusValue);
        setValue(0);
        dispatch(
          showToast({
            type: "success",
            message: type === 1 ? "Order approved successfully!" : "Order rejected successfully!",
          })
        );
      }
      setLoading(false);
    });
  };

  return (
    <>
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Backdrop sx={{ color: "red", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>
        <Box
          sx={{
            overflow: "hidden",
          }}
        >
          <Paper className={classes.paper}>
            <Grid container spacing={2} height="100%">
              <Grid item xs={12} sm={12} md={12} lg={6} xl={6} className={classes.gridItem}>
                <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }}>
                  <h3 className={classes.detailsHeader}>
                    <b> Order </b>
                  </h3>

                  {role != 3 && role != 4 ? (
                    <Box>
                      <InputLabel sx={{ mt: 1 }} className="inputFormCustomLabel">
                        Amount
                      </InputLabel>
                      <TextField
                        type="number"
                        margin="normal"
                        defaultValue={amount}
                        inputRef={amountRef}
                        fullWidth
                        variant="filled"
                        placeholder="Enter Amount Here"
                        hiddenLabel
                        size="small"
                        sx={{ mt: 1 }}
                        error={errors.amount}
                        helperText={errors.amount ? "Please enter amount!" : ""}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Box>
                  ) : (
                    <List sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper", pt: 0, pb: 0 }}>
                      <ListItem alignItems="flex-start" sx={{ pl: 0 }}>
                        <ListItemText primary="Amount:" secondary={"$ " + amount} />
                      </ListItem>

                      <ListItem alignItems="flex-start" sx={{ pl: 0 }}>
                        <ListItemText primary="Description:" secondary={orderDescription} />
                      </ListItem>
                    </List>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={6} xl={6} className={classes.gridItem}>
                {role != 3 && role != 4 ? (
                  <>
                    <InputLabel sx={{ mt: 1 }} className="inputFormCustomLabel">
                      Description
                    </InputLabel>
                    <TextField
                      margin="normal"
                      defaultValue={orderDescription}
                      inputRef={orderDescriptionRef}
                      fullWidth
                      variant="filled"
                      placeholder="Enter Order Description Here"
                      hiddenLabel
                      sx={{ mt: 1 }}
                      error={errors.description}
                      helperText={errors.description ? "Please enter Description!" : ""}
                      multiline
                      rows={5}
                    />
                  </>
                ) : (
                  <>
                    <InputLabel sx={{ mt: 1 }} className="inputFormCustomLabel">
                      Comments
                    </InputLabel>
                    <TextField
                      margin="normal"
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      fullWidth
                      variant="filled"
                      placeholder="Enter Comments Here"
                      hiddenLabel
                      sx={{ mt: 1 }}
                      multiline
                      rows={5}
                    />
                  </>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
        <Box sx={{ flex: "1 1 auto" }} />

        {role !== 3 && role !== 4 ? (
          <Button variant="contained" size="small" onClick={handleOrder} style={{ padding: "5px 20px" }}>
            Order
          </Button>
        ) : (
          <>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => handleApproveRejectOrder(0)}
              style={{ padding: "5px 20px", marginRight: "15px" }}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => handleApproveRejectOrder(1)}
              style={{ backgroundColor: "#1b5e20", padding: "5px 20px" }}
            >
              Approve
            </Button>
          </>
        )}
      </Box>
      <HistoryTable columns={columns} history={history} historyLoading={historyLoading} />
    </>
  );
};

export default Order;
